import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import axiosInstance from '../../services/axios.config';
import { publishMissionPreview, CompiledMission } from '../../services/mission-preview';

interface FindTrailerProps {
    onClose: () => void;
}

function FindTrailer({ onClose }: FindTrailerProps): React.ReactElement {
    const [trailerId, setTrailerId] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleFindTrailer = async () => {
        const trailer = trailerId.trim();

        if (!trailer || loading) return;

        setLoading(true);
        setError('');

        try {
            const response = await axiosInstance.post(
                '/yards/find-trailer/generate',
                { trailer_id: trailer }
            );

            if (response.data?.success !== true || !response.data?.mission) {
                throw new Error(
                    response.data?.error || 'Find Trailer route generation failed'
                );
            }

            const compiled = await axiosInstance.post<CompiledMission>(
                '/yards/mission/compile',
                { mission: response.data.mission }
            );

            if (compiled.data?.success !== true) {
                throw new Error('Find Trailer mission validation failed');
            }

            publishMissionPreview(compiled.data);
            onClose();
        } catch (err: any) {
            setError(
                err.response?.data?.error ||
                err.message ||
                'Failed to generate Find Trailer flight preview'
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="w-full max-w-[720px] rounded-lg bg-white shadow-xl">

                {/* Header */}
                <div className="flex items-start justify-between border-b px-6 py-5">
                    <div>
                        <h2 className="font-[Geist-Bold] text-xl text-green">
                            Find Trailer
                        </h2>

                        <p className="mt-1 text-sm text-gray-2">
                            Locate a trailer in the yard or initiate a drone search.
                        </p>
                    </div>

                    <button
                        onClick={onClose}
                        className="text-gray-2 transition hover:text-black"
                        aria-label="Close Find Trailer"
                    >
                        <Icon icon="material-symbols:close" className="text-2xl" />
                    </button>
                </div>

                {/* Content */}
                <div className="px-6 py-6">
                    <label className="mb-2 block font-[Geist-SemiBold] text-sm text-black-1">
                        Trailer ID
                    </label>

                    <input
                        type="text"
                        value={trailerId}
                        onChange={(event) => setTrailerId(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                handleFindTrailer();
                            }
                        }}
                        placeholder="Enter Trailer ID"
                        autoFocus
                        className="w-full rounded border border-gray-3 px-4 py-3 text-black-1 outline-none transition focus:border-blue"
                    />

                    <div className="mt-5 rounded-lg border border-gray-3 bg-gray-50 p-4">
                        <div className="flex items-start gap-3">
                            <Icon
                                icon="material-symbols:location-searching"
                                className="mt-0.5 text-2xl text-blue"
                            />

                            <div>
                                <div className="font-[Geist-SemiBold] text-sm text-black-1">
                                    Yard Location Search
                                </div>

                                <div className="mt-1 text-sm text-gray-2">
                                    Willow Yard will check the trailer's expected parking location.
                                    If the trailer cannot be confirmed, a drone search can be initiated.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {error && (
                    <div role="alert" className="mx-6 mb-4 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">
                        {error}
                    </div>
                )}

                {/* Footer */}
                <div className="flex justify-end gap-3 border-t px-6 py-4">
                    <button
                        onClick={onClose}
                        className="rounded border border-gray-3 px-5 py-2 text-sm font-[Geist-SemiBold] text-black-1 transition hover:bg-gray-50"
                    >
                        Cancel
                    </button>

                    <button
                        onClick={handleFindTrailer}
                        disabled={!trailerId.trim() || loading}
                        className="flex items-center gap-2 rounded bg-blue px-5 py-2 text-sm font-[Geist-SemiBold] text-white transition hover:bg-blue-1 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <Icon icon="material-symbols:search" className="text-lg" />
                        {loading ? "Generating Preview..." : "Generate Flight Preview"}
                    </button>
                </div>

            </div>
        </div>
    );
}

export default FindTrailer;
